/**
 * Game Detail Page — matches the original GamePoint game page layout.
 * Layout: Header with title/status/actions → Stat cards row →
 *   Main (2-col): Left = Prize Distribution + Activity + Submissions | Right = Host + Players
 *   Floating chat button + slide-out chat panel
 */

import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Link, useParams, useSearch, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Trophy, Users, DollarSign, Clock, Share2,
  Star, ExternalLink, Copy, Check, Image, FileText, Video, Link2, Hash,
  TrendingUp, Megaphone, ChevronUp, ImageDown, MessageCircle,
  X, Send, Crown, UserPlus, LogOut, Globe, MoreHorizontal,
  Activity, Zap, Eye, Play, Ban, AlertCircle, UserCheck, Music, Swords,
  Twitter, Facebook, MessageSquare, Flag
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import ShareCardModal from "@/components/ShareCardModal";
import PostGameReview from "@/components/PostGameReview";
import SocialProofDashboard from "@/components/SocialProofDashboard";
import LinkSocialPostForm from "@/components/LinkSocialPostForm";
import SubmissionUploadForm from "@/components/SubmissionUploadForm";
import SafeImage from "@/components/SafeImage";
import { useState, useMemo, useRef, useEffect } from "react";
import { toast } from "sonner";
import { getLoginUrl } from "@/const";

// ─── Status Helpers ──────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: "DRAFT", color: "text-muted-foreground", bg: "bg-muted" },
  active: { label: "RECRUITING", color: "text-emerald-400", bg: "bg-emerald-500/20" },
  voting: { label: "LIVE", color: "text-gold", bg: "bg-gold/20" },
  completed: { label: "SETTLED", color: "text-blue-400", bg: "bg-blue-500/20" },
  cancelled: { label: "CANCELLED", color: "text-red-400", bg: "bg-red-500/20" },
};

// ─── Activity Icon Map ───────────────────────────────────────────

const ACTIVITY_ICONS: Record<string, typeof Activity> = {
  game_published: Play,
  game_started: Zap,
  game_ended: Trophy,
  game_cancelled: Ban,
  player_joined: UserPlus,
  player_left: LogOut,
  player_refunded: DollarSign,
  submission_added: Image,
  submission_featured: Star,
  vote_cast: ChevronUp,
  status_changed: AlertCircle,
  config_updated: Activity,
};

// ─── Submission Type Icons ───────────────────────────────────────

const SUBMISSION_TYPE_ICONS: Record<string, typeof Image> = {
  image: Image,
  text: FileText,
  video: Video,
  link: Link2,
  numeric: Hash,
};

// ─── Countdown Hook ──────────────────────────────────────────────

function useCountdown(endDate: Date | null) {
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!endDate) { setTimeLeft("No End Date"); return; }

    const update = () => {
      const now = Date.now();
      const end = endDate.getTime();
      const diff = end - now;

      if (diff <= 0) { setTimeLeft("Closed"); return; }

      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);

      if (days > 0) setTimeLeft(`${days}d ${hours}h`);
      else if (hours > 0) setTimeLeft(`${hours}h ${minutes}m`);
      else setTimeLeft(`${minutes}m ${seconds}s`);
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [endDate]);

  return timeLeft;
}

// ─── Submission Card ─────────────────────────────────────────────

function SubmissionCard({
  submission,
  gameSlug,
  onVote,
  isVoting,
  refToken,
}: {
  submission: {
    id: number;
    content: string;
    submissionType: string;
    caption: string | null;
    voteCount: number;
    shareToken: string;
    featured: number;
    originalFileName?: string | null;
    mimeType?: string | null;
    fileSize?: number | null;
    galleryFiles?: Array<{ url: string; fileName: string; mimeType: string; fileSize: number }> | null;
  };
  gameSlug: string;
  onVote: (id: number) => void;
  isVoting: boolean;
  refToken?: string;
}) {
  const [copied, setCopied] = useState(false);
  const [showShareCard, setShowShareCard] = useState(false);
  const shareUrl = `${window.location.origin}/game/${gameSlug}/entry/${submission.shareToken}`;
  const Icon = SUBMISSION_TYPE_ICONS[submission.submissionType] || FileText;

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-panel rounded-xl overflow-hidden group"
    >
      <div className="relative">
        {submission.featured === 1 && (
          <div className="absolute top-2 right-2 z-10">
            <Badge className="bg-gold text-background text-xs gap-1">
              <Star className="w-3 h-3" /> Featured
            </Badge>
          </div>
        )}

        {/* Gallery mode: multiple images */}
        {submission.galleryFiles && submission.galleryFiles.length > 1 ? (
          <div className="grid grid-cols-2 gap-0.5 aspect-square overflow-hidden">
            {submission.galleryFiles.slice(0, 4).map((file, i) => (
              <div key={i} className="relative bg-secondary/50 overflow-hidden">
                {file.mimeType.startsWith("image/") ? (
                  <SafeImage src={file.url} alt={file.fileName} className="w-full h-full object-cover" />
                ) : file.mimeType.startsWith("video/") ? (
                  <video src={file.url} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                {i === 3 && submission.galleryFiles!.length > 4 && (
                  <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                    <span className="text-lg font-bold">+{submission.galleryFiles!.length - 4}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : submission.submissionType === "image" ? (
          <div className="aspect-square bg-secondary/50 flex items-center justify-center overflow-hidden">
            <SafeImage
              src={submission.content}
              alt={submission.caption || "Submission"}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ) : submission.submissionType === "video" ? (
          <div className="aspect-video bg-secondary/50">
            <video src={submission.content} controls className="w-full h-full object-cover" />
          </div>
        ) : submission.submissionType === "audio" ? (
          <div className="p-6 min-h-[140px] bg-gradient-to-br from-purple-500/10 to-teal/10 flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Music className="w-8 h-8 text-purple-400" />
            </div>
            <audio src={submission.content} controls className="w-full max-w-[280px]" />
            {submission.originalFileName && (
              <p className="text-xs text-muted-foreground truncate max-w-full">{submission.originalFileName}</p>
            )}
          </div>
        ) : submission.submissionType === "document" ? (
          <div className="p-6 min-h-[140px] bg-gradient-to-br from-blue-500/10 to-teal/10 flex flex-col items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center">
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-sm font-medium truncate max-w-full">
              {submission.originalFileName || "Document"}
            </p>
            {submission.fileSize && (
              <p className="text-xs text-muted-foreground">
                {(submission.fileSize / (1024 * 1024)).toFixed(1)}MB
              </p>
            )}
            <a
              href={submission.content}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-neon hover:underline flex items-center gap-1"
            >
              <ExternalLink className="w-3 h-3" /> View / Download
            </a>
          </div>
        ) : (
          <div className="p-6 min-h-[120px] bg-secondary/30 flex items-center">
            <div className="flex items-start gap-3">
              <Icon className="w-5 h-5 text-neon shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/90 line-clamp-4">{submission.content}</p>
            </div>
          </div>
        )}
      </div>

      {submission.caption && (
        <div className="px-4 pt-3">
          <p className="text-xs text-muted-foreground line-clamp-2">{submission.caption}</p>
        </div>
      )}

      <div className="p-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 border-neon/30 hover:bg-neon/10 hover:border-neon/50 hover:text-neon"
          onClick={() => onVote(submission.id)}
          disabled={isVoting}
        >
          <ChevronUp className="w-4 h-4" />
          <span className="font-mono font-bold">{submission.voteCount}</span>
        </Button>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground hover:text-gold px-2"
            onClick={() => setShowShareCard(true)}
            title="Generate share card"
          >
            <ImageDown className="w-3.5 h-3.5" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground hover:text-neon">
                {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
                <span className="text-xs">Share</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={copyShareLink}>
                <Copy className="w-4 h-4 mr-2" />
                {copied ? "Copied!" : "Copy Link"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out this entry!')}&url=${encodeURIComponent(shareUrl)}`, '_blank')}>
                <Twitter className="w-4 h-4 mr-2" />
                Share on X
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank')}>
                <Facebook className="w-4 h-4 mr-2" />
                Share on Facebook
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out this entry! ${shareUrl}`)}`, '_blank')}>
                <MessageSquare className="w-4 h-4 mr-2" />
                WhatsApp
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <ShareCardModal
        open={showShareCard}
        onOpenChange={setShowShareCard}
        submissionId={submission.id}
        gameSlug={gameSlug}
        shareToken={submission.shareToken}
      />
    </motion.div>
  );
}

// ─── Chat Panel ──────────────────────────────────────────────────

function ChatPanel({
  gameId,
  isOpen,
  onClose,
}: {
  gameId: number;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const utils = trpc.useUtils();

  const { data: messages, isLoading } = trpc.game.chatMessages.useQuery(
    { gameId },
    { enabled: isOpen, refetchInterval: 5000 }
  );

  const sendMutation = trpc.game.sendChat.useMutation({
    onSuccess: () => {
      setMessage("");
      utils.game.chatMessages.invalidate({ gameId });
      utils.game.chatCount.invalidate({ gameId });
    },
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!message.trim() || !user || sendMutation.isPending) return;
    sendMutation.mutate({ gameId, message: message.trim() });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed top-0 right-0 h-full w-80 sm:w-96 z-50 glass-panel border-l border-border/50 flex flex-col shadow-2xl"
        >
          {/* Chat Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-neon" />
              <span className="font-semibold text-sm">Chat</span>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="h-7 w-7 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-neon border-t-transparent rounded-full animate-spin" />
              </div>
            ) : messages && messages.length > 0 ? (
              messages.map((msg) => (
                <div key={msg.id} className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`text-[10px] px-1.5 py-0 ${
                        msg.role === "host"
                          ? "border-gold/40 text-gold"
                          : msg.role === "system"
                          ? "border-neon/40 text-neon"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {msg.role === "host" ? "HOST" : msg.role === "system" ? "SYSTEM" : "PLAYER"}
                    </Badge>
                    <span className="text-xs font-medium">{msg.userName || "Anonymous"}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className="text-sm text-foreground/90 pl-1">{msg.message}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No messages yet. Start the conversation!
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-3 border-t border-border/50">
            {user ? (
              <div className="flex items-center gap-2">
                <Input
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="text-sm bg-secondary/50"
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <Button
                  size="sm"
                  className="bg-neon hover:bg-neon-light text-background h-9 px-3"
                  onClick={handleSend}
                  disabled={!message.trim() || sendMutation.isPending}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <a href={getLoginUrl()} className="block text-center text-sm text-neon hover:underline">
                Sign in to chat
              </a>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Prize Distribution Visualization ────────────────────────────

function PrizeDistribution({ game }: { game: any }) {
  const PLATFORM_FEE = 15;
  // These figures are projections based on a full game, not confirmed funds.
  const totalPool = (game.entryFee / 100) * game.maxPlayers;
  const platformAmount = totalPool * (PLATFORM_FEE / 100);
  const hostAmount = totalPool * (game.hostCut / 100);
  const promoterAmount = totalPool * (game.promoterCut / 100);
  const winnerPercent = 100 - PLATFORM_FEE - game.hostCut - game.promoterCut;
  const winnerAmount = totalPool * (winnerPercent / 100);

  // Simple pie chart using conic-gradient
  // Re-normalize to visible pool (exclude platform fee from chart)
  const visibleTotal = winnerPercent + game.hostCut + game.promoterCut;
  const hostDeg = (game.hostCut / visibleTotal) * 360;
  const promoterDeg = (game.promoterCut / visibleTotal) * 360;

  return (
    <Card className="glass-panel border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Trophy className="w-4 h-4 text-gold" />
          Potential Prize Distribution
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          At full capacity, this is how projected entry revenue would be distributed.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-6">
          {/* Revenue Allocation */}
          <div>
            <p className="text-sm font-medium mb-1">Potential Revenue Allocation</p>
            <p className="text-xs text-muted-foreground mb-3">At full capacity: ${(totalPool - platformAmount).toFixed(2)}</p>
            <div
              className="w-32 h-32 mx-auto rounded-full"
              style={{
                background: `conic-gradient(
                  oklch(0.72 0.15 195) 0deg ${(winnerPercent / visibleTotal) * 360}deg,
                  oklch(0.80 0.16 85) ${(winnerPercent / visibleTotal) * 360}deg ${(winnerPercent / visibleTotal) * 360 + promoterDeg}deg,
                  oklch(0.65 0.18 340) ${(winnerPercent / visibleTotal) * 360 + promoterDeg}deg 360deg
                )`,
              }}
            />
            <div className="mt-3 space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-neon" />
                <span>Winner <span className="font-mono font-bold text-neon">${winnerAmount.toFixed(2)}</span>
                  <span className="text-muted-foreground">({winnerPercent}%)</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-gold" />
                <span>Promoter <span className="font-mono font-bold text-gold">${promoterAmount.toFixed(2)}</span>
                  <span className="text-muted-foreground">({game.promoterCut}%)</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: "oklch(0.65 0.18 340)" }} />
                <span>Host <span className="font-mono font-bold">${hostAmount.toFixed(2)}</span>
                  <span className="text-muted-foreground">({game.hostCut}%)</span>
                </span>
              </div>
            </div>
          </div>

          {/* Rank Payout */}
          <div>
            <p className="text-sm font-medium mb-1">Rank Payout</p>
            <p className="text-xs text-muted-foreground mb-3">
              Winner Pool: ${winnerAmount.toFixed(2)} ({winnerPercent}%)
            </p>
            <div
              className="w-32 h-32 mx-auto rounded-full bg-gold"
              title="Winner takes all"
            />
            <div className="mt-3 space-y-1 text-xs">
              <p className="text-muted-foreground">Winner takes all</p>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-gold" />
                <span>#1 <span className="font-mono font-bold text-gold">${winnerAmount.toFixed(2)}</span>
                  <span className="text-muted-foreground">(100%)</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Activity Timeline ───────────────────────────────────────────

function ActivityTimeline({ gameId }: { gameId: number }) {
  const { data: activities, isLoading } = trpc.game.activityFeed.useQuery(
    { gameId, limit: 30 },
    { refetchInterval: 15000 }
  );

  return (
    <Card className="glass-panel border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-neon" />
          Game Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="w-4 h-4 border-2 border-neon border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activities && activities.length > 0 ? (
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
            {activities.map((act) => {
              const Icon = ACTIVITY_ICONS[act.action] || Activity;
              return (
                <div key={act.id} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-secondary/80 flex items-center justify-center shrink-0 mt-0.5">
                    <Icon className="w-3.5 h-3.5 text-neon" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">
                      <span className="font-medium">{act.userName || "System"}</span>{" "}
                      <span className="text-muted-foreground">
                        {act.action.replace(/_/g, " ")}
                      </span>
                    </p>
                    {act.details && typeof act.details === "object" && Object.keys(act.details).length > 0 && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {(act.details as any).paidAmount
                          ? `Entry: $${((act.details as any).paidAmount / 100).toFixed(2)}`
                          : JSON.stringify(act.details).slice(0, 80)}
                      </p>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {new Date(act.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">No activities yet</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Link Social Post Section ────────────────────────────────────

function LinkSocialPostSection({ submissions, gameId }: {
  submissions: { id: number; content: string; submissionType: string; voteCount: number }[];
  gameId: number;
}) {
  const [openSubId, setOpenSubId] = useState<number | null>(null);

  return (
    <div className="mt-8">
      <Card className="glass-panel border-gold/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-gold" />
            Link Your Social Post
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Posted your submission on social media? Link it here to earn social engagement points!
          </p>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {submissions.map((sub) => (
              <div key={sub.id} className="flex items-center justify-between gap-4 p-3 rounded-lg bg-secondary/30">
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">
                    {sub.submissionType === "image" ? "Image submission" : sub.content.slice(0, 60)}
                  </p>
                  <p className="text-xs text-muted-foreground">Votes: {sub.voteCount}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 border-gold/30 text-gold hover:bg-gold/10"
                  onClick={() => setOpenSubId(sub.id)}
                >
                  <Globe className="w-3.5 h-3.5" />
                  Link Post
                </Button>
                <LinkSocialPostForm
                  open={openSubId === sub.id}
                  onOpenChange={(open) => { if (!open) setOpenSubId(null); }}
                  submissionId={sub.id}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function GameDetail() {
  const params = useParams<{ slug: string }>();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const searchParams = new URLSearchParams(search);
  const refToken = searchParams.get("ref") || undefined;
  const paymentAttemptId = searchParams.get("payment_attempt") || undefined;
  const { user, isAuthenticated } = useAuth();

  const [chatOpen, setChatOpen] = useState(false);
  const [shareGameCopied, setShareGameCopied] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState<"incorrect_winner" | "rule_violation" | "payment_issue" | "other">("incorrect_winner");
  const [disputeDescription, setDisputeDescription] = useState("");
  const [joinCouponCode, setJoinCouponCode] = useState("");

  // ─── Data Fetching ──────────────────────────────────────────

  const { data: game, isLoading: gameLoading } = trpc.game.getDetail.useQuery(
    { slug: params.slug || "" },
    { enabled: !!params.slug }
  );

  const { data: submissions, isLoading: subsLoading } = trpc.game.getSubmissions.useQuery(
    { gameId: game?.id || 0, orderByVotes: true },
    { enabled: !!game?.id }
  );

  const { data: players } = trpc.game.players.useQuery(
    { gameId: game?.id || 0 },
    { enabled: !!game?.id }
  );

  const { data: isJoined } = trpc.game.isJoined.useQuery(
    { gameId: game?.id || 0, userId: user?.id },
    { enabled: !!game?.id && !!user?.id }
  );

  const { data: chatCount } = trpc.game.chatCount.useQuery(
    { gameId: game?.id || 0 },
    { enabled: !!game?.id, refetchInterval: 10000 }
  );

  const { data: myReferralCode } = trpc.referralCode.getMyCode.useQuery(
    undefined,
    { enabled: !!user }
  );

  const voteMutation = trpc.game.vote.useMutation();
  const joinMutation = trpc.game.startEntry.useMutation();
  const leaveMutation = trpc.game.leave.useMutation();
  const statusUpdateMutation = trpc.game.updateStatus.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(`Game moved to "${variables.status}". Players have been notified.`);
      utils.game.getDetail.invalidate({ slug: params.slug });
      utils.game.activityFeed.invalidate({ gameId: game?.id });
      utils.game.players.invalidate({ gameId: game?.id });
    },
    onError: (err) => toast.error(err.message || "Failed to update game status"),
  });
  const disputeMutation = trpc.dispute.file.useMutation({
    onSuccess: () => toast.success("Outcome flagged for admin review. We'll look into it."),
    onError: (err) => toast.error(err.message || "Failed to submit dispute"),
  });
  const utils = trpc.useUtils();
  const paymentAttempt = trpc.stripePayment.getAttempt.useQuery(
    { paymentAttemptId: paymentAttemptId ?? "invalid" },
    { enabled: Boolean(paymentAttemptId && user), refetchInterval: query => ["creating", "open", "pending"].includes(query.state.data?.status ?? "") ? 3000 : false }
  );

  useEffect(() => {
    if (!paymentAttemptId || !paymentAttempt.data || !game) return;
    if (paymentAttempt.data.status === "paid") {
      toast.success("Payment confirmed — you have joined the game.");
      utils.game.players.invalidate({ gameId: game.id });
      utils.game.isJoined.invalidate({ gameId: game.id });
      utils.game.activityFeed.invalidate({ gameId: game.id });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (["failed", "cancelled", "expired"].includes(paymentAttempt.data.status)) {
      toast.error("Your game entry payment was not completed.", { description: paymentAttempt.data.failureReason ?? undefined });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [paymentAttemptId, paymentAttempt.data?.status, game?.id]);

  // ─── Derived Values ─────────────────────────────────────────

  const prizePool = useMemo(() => {
    if (!game) return 0;
    // Projection at maximum capacity; confirmed entries determine settlement.
    return (game.entryFee / 100) * game.maxPlayers * ((100 - 15 - game.hostCut - game.promoterCut) / 100);
  }, [game]);

  const statusConfig = game ? STATUS_CONFIG[game.status] || STATUS_CONFIG.draft : STATUS_CONFIG.draft;
  const timeLeft = useCountdown(game?.endsAt ? new Date(game.endsAt) : null);
  const activePlayers = players?.filter((p) => p.status === "joined") || [];
  const playerProgress = game ? (activePlayers.length / game.maxPlayers) * 100 : 0;
  const isHost = !!user && !!game && (game.creatorId === user.id || user.role === "admin");

  // ─── Handlers ───────────────────────────────────────────────

  const handleVote = async (submissionId: number) => {
    try {
      await voteMutation.mutateAsync({ submissionId, referralShareToken: refToken });
      toast.success("Vote recorded!");
      utils.game.getSubmissions.invalidate({ gameId: game?.id });
    } catch (err: any) {
      if (err?.message?.includes("already voted")) {
        toast.error("You've already voted for this submission");
      } else {
        toast.error("Failed to vote. Please try again.");
      }
    }
  };

  const handleJoin = async () => {
    if (!game || !user) return;
    try {
      const result = await joinMutation.mutateAsync({
        gameId: game.id,
        couponCode: joinCouponCode.trim() || undefined,
        referralCode: refToken,
      });
      if (result.status === "checkout_required") {
        window.location.assign(result.checkoutUrl);
        return;
      }
      if (result.status === "pending") {
        toast.info("Your entry payment is already being confirmed. Please wait a moment before trying again.");
        return;
      }
      toast.success(
        "You've joined the game!"
      );
      setJoinCouponCode("");
      utils.game.players.invalidate({ gameId: game.id });
      utils.game.isJoined.invalidate({ gameId: game.id });
      utils.game.getDetail.invalidate({ slug: params.slug || "" });
      utils.game.activityFeed.invalidate({ gameId: game.id });
    } catch (err: any) {
      toast.error(err?.message || "Failed to join");
    }
  };

  const handleLeave = async () => {
    if (!game || !user) return;
    if (game.entryFee > 0 && !window.confirm("Leaving a paid event does not refund your ticket. Continue?")) return;
    try {
      await leaveMutation.mutateAsync({ gameId: game.id });
      toast.success("You've left the game");
      utils.game.players.invalidate({ gameId: game.id });
      utils.game.isJoined.invalidate({ gameId: game.id });
      utils.game.activityFeed.invalidate({ gameId: game.id });
      utils.game.getDetail.invalidate({ slug: params.slug || "" });
    } catch (err: any) {
      toast.error(err?.message || "Failed to leave");
    }
  };

  const copyGameLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setShareGameCopied(true);
    toast.success("Game link copied!");
    setTimeout(() => setShareGameCopied(false), 2000);
  };

  // ─── Loading / Not Found ────────────────────────────────────

  if (gameLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="w-5 h-5 border-2 border-neon border-t-transparent rounded-full animate-spin" />
          Loading game...
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <h2 className="text-2xl font-bold">Game Not Found</h2>
          <Link href="/">
            <Button variant="outline">Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const canJoin = game.status === "active" && !isJoined && activePlayers.length < game.maxPlayers;
  // Host lifecycle actions. Server rules: paid games complete via settlement,
  // and only admins may (re)activate or cancel paid games.
  const canStartVoting = isHost && game.status === "active";
  const canComplete = isHost && (game.status === "voting" || game.status === "active") && game.entryFee === 0;
  const showHostPanel = isHost && (game.status === "draft" || canStartVoting || canComplete);
  const promoterLink = myReferralCode?.code
    ? `${window.location.origin}/game/${game.slug}?ref=${myReferralCode.code}`
    : "";

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Top Header ─────────────────────────────────────── */}
      <header className="sticky top-0 z-40 glass-panel border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm hidden sm:inline">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 relative"
              onClick={() => setChatOpen(true)}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Chat
              {(chatCount ?? 0) > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
                  {chatCount}
                </span>
              )}
            </Button>
            {isAuthenticated && (
              <Link href={`/console/challenges?game=${encodeURIComponent(game.title)}`}>
                <Button variant="outline" size="sm" className="gap-1.5 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  <Swords className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Challenge</span>
                </Button>
              </Link>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Share2 className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Share</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={copyGameLink}>
                  <Copy className="w-4 h-4 mr-2" />
                  {shareGameCopied ? "Copied!" : "Copy Link"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out this competition on GamePoint! ${game.title}`)}&url=${encodeURIComponent(window.location.href)}`, '_blank')}>
                  <Twitter className="w-4 h-4 mr-2" />
                  Share on X
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}>
                  <Facebook className="w-4 h-4 mr-2" />
                  Share on Facebook
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`${game.title} - Compete and win! ${window.location.href}`)}`, '_blank')}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Share on WhatsApp
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge className={`${statusConfig.bg} ${statusConfig.color} border-0 text-xs font-bold`}>
              {statusConfig.label}
            </Badge>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* ─── Game Title & Tags ──────────────────────────── */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">{game.title}</h1>
          <div className="flex flex-wrap items-center gap-2">
            {game.gameType && (
              <Badge variant="outline" className="text-xs border-border">{game.gameType}</Badge>
            )}
            <Badge variant="outline" className="text-xs border-border">Online</Badge>
            <Badge variant="outline" className="text-xs border-border">
              {game.votingMethod === "community_vote" ? "Solo Mode" : game.votingMethod.replace(/_/g, " ")}
            </Badge>
          </div>
        </div>

        {/* ─── Stat Cards Row ─────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {/* Potential prize at maximum capacity */}
          <div className="rounded-xl p-4 bg-emerald-600/90 text-white">
            <div className="flex items-center gap-1.5 text-xs font-medium opacity-90 mb-1">
              <Trophy className="w-3.5 h-3.5" /> Potential Prize
            </div>
            <p className="text-2xl sm:text-3xl font-bold font-mono">${prizePool.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[10px] font-medium opacity-80 mt-1">at full capacity</p>
          </div>

          {/* Ticket price */}
          <div className="rounded-xl p-4 bg-yellow-500/90 text-black">
            <div className="flex items-center gap-1.5 text-xs font-medium opacity-80 mb-1">
              <DollarSign className="w-3.5 h-3.5" /> Ticket price
            </div>
            <p className="text-2xl sm:text-3xl font-bold font-mono">${(game.entryFee / 100).toFixed(2)}</p>
          </div>

          {/* Players + Spots Remaining FOMO */}
          <div className="rounded-xl p-4 bg-blue-600/90 text-white">
            <div className="flex items-center justify-between text-xs font-medium opacity-90 mb-1">
              <div className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> Players
              </div>
              <span>Progress <span className="font-mono">{Math.round(playerProgress)}%</span></span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold font-mono">{activePlayers.length}/{game.maxPlayers}</p>
            <div className="w-full h-1.5 bg-white/20 rounded-full mt-2 overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${playerProgress}%` }}
              />
            </div>
            {game.maxPlayers - activePlayers.length > 0 && game.maxPlayers - activePlayers.length <= Math.ceil(game.maxPlayers * 0.2) && (
              <p className="text-xs font-bold mt-1.5 animate-pulse text-yellow-200">
                Only {game.maxPlayers - activePlayers.length} spot{game.maxPlayers - activePlayers.length === 1 ? '' : 's'} left!
              </p>
            )}
            {game.maxPlayers - activePlayers.length > Math.ceil(game.maxPlayers * 0.2) && (
              <p className="text-xs mt-1.5 opacity-80">
                {game.maxPlayers - activePlayers.length} spots remaining
              </p>
            )}
          </div>

          {/* Time Left */}
          <div className="rounded-xl p-4 bg-secondary/80 text-foreground">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
              <Clock className="w-3.5 h-3.5" /> Time Left
            </div>
            <p className="text-2xl sm:text-3xl font-bold font-mono">{timeLeft}</p>
          </div>
        </div>

        {/* ─── Host Lifecycle Controls ────────────────────── */}
        {showHostPanel && (
          <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-semibold text-foreground">Host controls</span>
            </div>
            {game.status === "draft" && (
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-amber-400 shrink-0" />
                {game.entryFee > 0
                  ? "This paid game is awaiting administrator approval before it goes live."
                  : "This game is a draft. Publish it from the creation wizard to accept players."}
              </p>
            )}
            {(canStartVoting || canComplete) && (
              <div className="flex flex-wrap items-center gap-2">
                {canStartVoting && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-neon/40 text-neon hover:bg-neon/10"
                    disabled={statusUpdateMutation.isPending}
                    onClick={() => statusUpdateMutation.mutate({ gameId: game.id, status: "voting" })}
                  >
                    <Play className="w-3.5 h-3.5 mr-1" />
                    Start Voting
                  </Button>
                )}
                {canComplete && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={statusUpdateMutation.isPending}
                    onClick={() => {
                      if (window.confirm("Complete this game? Winners are determined by current vote counts.")) {
                        statusUpdateMutation.mutate({ gameId: game.id, status: "completed" });
                      }
                    }}
                  >
                    <Check className="w-3.5 h-3.5 mr-1" />
                    Complete Game
                  </Button>
                )}
                {game.entryFee > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Paid games are completed through the settlement workflow (Console → Settles).
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* ─── Main Content: 2-Column Layout ──────────────── */}
        <div className="grid lg:grid-cols-[1fr_280px] gap-6">
          {/* LEFT COLUMN */}
          <div className="space-y-6">
            {/* Game Description & Rules */}
            {(game.description || game.winCondition || (game.rules as string[])?.length) && (
              <Card className="glass-panel border-border/50">
                <CardContent className="pt-6 space-y-4">
                  {game.description && (
                    <p className="text-sm text-foreground/90 leading-relaxed">{game.description}</p>
                  )}
                  {game.winCondition && (
                    <div className="rounded-lg p-3 bg-neon/5 border border-neon/20">
                      <p className="text-xs text-neon font-medium mb-1 uppercase tracking-wider">How to Win</p>
                      <p className="text-sm text-foreground/90">{game.winCondition}</p>
                    </div>
                  )}
                  {game.rules && (game.rules as string[]).length > 0 && (
                    <div className="rounded-lg p-3 bg-secondary/30">
                      <p className="text-xs text-neon font-medium mb-2 uppercase tracking-wider">Rules</p>
                      <ul className="space-y-1">
                        {(game.rules as string[]).map((rule, i) => (
                          <li key={i} className="text-sm text-foreground/80 flex items-start gap-2">
                            <span className="text-neon font-mono text-xs mt-0.5">{i + 1}.</span>
                            {rule}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {game.influencerChannelUrl && (
                    <a
                      href={game.influencerChannelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gold/10 border border-gold/20 text-gold hover:bg-gold/20 transition-colors text-sm"
                    >
                      <Megaphone className="w-4 h-4" />
                      Visit Creator's Channel
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Prize Distribution */}
            <PrizeDistribution game={game} />

            {/* Activity Timeline */}
            <ActivityTimeline gameId={game.id} />

            {/* Submission Form — shown when user is logged in and game is active */}
            {user && game.status === "active" && isJoined && (
              <div>
                <SubmissionUploadForm
                  gameId={game.id}
                  submissionType={(game.submissionType as any) || "image"}
                  gameName={game.title}
                  onSuccess={() => utils.game.getSubmissions.invalidate({ gameId: game.id })}
                />
              </div>
            )}

            {/* Submission Gallery */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">
                  Submissions
                  <span className="text-muted-foreground font-normal text-sm ml-2">
                    ({submissions?.length ?? 0} entries)
                  </span>
                </h2>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ChevronUp className="w-3.5 h-3.5 text-neon" />
                  Vote for your favorite!
                </div>
              </div>

              {subsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-5 h-5 border-2 border-neon border-t-transparent rounded-full animate-spin" />
                </div>
              ) : submissions && submissions.length > 0 ? (
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  <AnimatePresence>
                    {submissions.map((sub) => (
                      <SubmissionCard
                        key={sub.id}
                        submission={sub}
                        gameSlug={game.slug}
                        onVote={handleVote}
                        isVoting={voteMutation.isPending}
                        refToken={refToken}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="text-center py-12 glass-panel rounded-xl">
                  <div className="w-14 h-14 rounded-full bg-neon/10 flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-7 h-7 text-neon" />
                  </div>
                  <h3 className="text-base font-semibold mb-1">No Submissions Yet</h3>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto">
                    Be the first to submit your entry!
                  </p>
                </div>
              )}
            </div>

            {/* Post-Game Reviews — shown when game is completed */}
            {user && game.status === "completed" && isJoined && (
              <PostGameReview gameId={game.id} gameTitle={game.title} />
            )}

            {/* Flag Outcome — shown when game is completed and user participated */}
            {user && game.status === "completed" && isJoined && (
              <Card className="glass-panel border-border/50">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <AlertCircle className="w-4 h-4 text-amber-400" />
                      <span>Something wrong with the outcome?</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-amber-400 gap-1.5 text-xs"
                      onClick={() => setDisputeOpen(true)}
                    >
                      <Flag className="w-3.5 h-3.5" />
                      Flag Outcome
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Dispute Dialog */}
            <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
              <DialogContent className="glass-panel border-border/50 sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-amber-400" />
                    Flag Game Outcome
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <p className="text-sm text-muted-foreground">
                    If you believe the outcome of this game was incorrect or unfair, let us know. An admin will review your report.
                  </p>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Reason</label>
                    <Select value={disputeReason} onValueChange={(v) => setDisputeReason(v as typeof disputeReason)}>
                      <SelectTrigger className="bg-muted/50 border-border/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="incorrect_winner">Incorrect Winner</SelectItem>
                        <SelectItem value="rule_violation">Rule Violation</SelectItem>
                        <SelectItem value="payment_issue">Payment Issue</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Description <span className="text-muted-foreground">(min 20 characters)</span></label>
                    <Textarea
                      value={disputeDescription}
                      onChange={(e) => setDisputeDescription(e.target.value)}
                      placeholder="Describe what went wrong..."
                      className="bg-muted/50 border-border/50 min-h-[100px] resize-none"
                      maxLength={2000}
                    />
                    <p className="text-xs text-muted-foreground text-right">{disputeDescription.length}/2000</p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setDisputeOpen(false)} disabled={disputeMutation.isPending}>
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    className="bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30"
                    onClick={() => {
                      if (disputeDescription.length < 20) {
                        toast.error("Please provide at least 20 characters of description.");
                        return;
                      }
                      disputeMutation.mutate({
                        gameId: game.id,
                        reason: disputeReason,
                        description: disputeDescription,
                      }, {
                        onSuccess: () => {
                          setDisputeOpen(false);
                          setDisputeDescription("");
                        }
                      });
                    }}
                    disabled={disputeMutation.isPending || disputeDescription.length < 20}
                  >
                    {disputeMutation.isPending ? "Submitting..." : "Submit Report"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Social Proof Leaderboard */}
            {game.socialDistributionMode === "hybrid" && game.id && (
              <SocialProofDashboard gameId={game.id} />
            )}

            {/* Link Social Post */}
            {game.socialDistributionMode === "hybrid" && user && submissions && submissions.length > 0 && (
              <LinkSocialPostSection submissions={submissions} gameId={game.id} />
            )}

            {/* Social Distribution Badge */}
            {game.socialDistributionMode && game.socialDistributionMode !== "direct" && (
              <div className="flex items-center justify-center gap-3 py-4">
                <Badge variant="outline" className="gap-1.5 border-gold/30 text-gold">
                  <Globe className="w-3 h-3" />
                  {game.socialDistributionMode === "hybrid" ? "Hybrid Scoring Active" : "API Connected"}
                </Badge>
                {game.socialHashtag && (
                  <Badge variant="outline" className="gap-1 font-mono border-neon/30 text-neon">
                    #{game.socialHashtag}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* RIGHT COLUMN — Sidebar */}
          <div className="space-y-4">
            {/* Host Card */}
            <Card className="glass-panel border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Crown className="w-4 h-4 text-gold" />
                  Host
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-neon/20 flex items-center justify-center text-neon font-bold text-sm">
                    {game.host?.name?.charAt(0)?.toUpperCase() || "H"}
                  </div>
                  <span className="font-medium text-sm">{game.host?.name || "Game Host"}</span>
                </div>
              </CardContent>
            </Card>

            {/* Players Card */}
            <Card className="glass-panel border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    Players
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">{activePlayers.length}</span>
                </CardTitle>
                <div className="w-full h-1 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${playerProgress}%` }}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {activePlayers.length > 0 ? (
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {activePlayers.map((p) => (
                      <div key={p.id} className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                          {p.userName?.charAt(0)?.toUpperCase() || "P"}
                        </div>
                        <span className="text-sm flex-1 truncate">{p.userName || "Player"}</span>
                        {user && p.userId !== user.id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-1.5 text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                            onClick={() => {
                              setLocation(`/console/challenges?challenge=${p.userId}&name=${encodeURIComponent(p.userName || "Player")}`);
                            }}
                            title={`Challenge ${p.userName || "Player"}`}
                          >
                            <Swords className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(p.joinedAt).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3">
                    No players yet. Be the first to join!
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Promoter / Referral Link: every signed-in member can promote, whether or not they play */}
            {user && (
              <Card className="glass-panel border-gold/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-gold" />
                    Your Promoter Link
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xs text-muted-foreground mb-2">
                    Share this link to earn promoter rewards when new players join!
                  </p>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={promoterLink || "Generating referral link..."}
                      className="text-xs bg-secondary/50 h-8"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2 border-gold/30 text-gold hover:bg-gold/10"
                      disabled={!promoterLink}
                      onClick={() => {
                        if (!promoterLink) return;
                        navigator.clipboard.writeText(promoterLink);
                        toast.success("Promoter link copied!");
                      }}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Viral Score */}
            {game.viralScore && game.viralScore > 0 && (
              <Card className="glass-panel border-neon/20">
                <CardContent className="pt-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-muted-foreground uppercase tracking-wider">Viral Potential</span>
                    <span className="font-mono font-bold text-neon text-sm">{game.viralScore}/100</span>
                  </div>
                  <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-teal to-gold transition-all duration-500"
                      style={{ width: `${game.viralScore}%` }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* ─── Bottom Join/Leave Bar ──────────────────────── */}
        <div className="sticky bottom-0 mt-8 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 glass-panel border-t border-border/50">
          {!user ? (
            <a href={getLoginUrl()} className="block">
              <Button className="w-full bg-neon hover:bg-neon-light text-background font-bold">
                Sign In to Join
              </Button>
            </a>
          ) : canJoin ? (
            <div className="space-y-2">
              {game.entryFee > 0 && (
                <Input
                  value={joinCouponCode}
                  onChange={(e) => setJoinCouponCode(e.target.value.toUpperCase())}
                  placeholder="Coupon code (optional)"
                  className="h-9 bg-secondary/50"
                  maxLength={64}
                />
              )}
              <Button
                className="w-full bg-neon hover:bg-neon-light text-background font-bold gap-2"
                onClick={handleJoin}
                disabled={joinMutation.isPending}
              >
                <UserPlus className="w-4 h-4" />
                {joinMutation.isPending ? "Joining..." : `Join Game - $${(game.entryFee / 100).toFixed(2)}`}
              </Button>
            </div>
          ) : isJoined ? (
            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 text-sm text-neon">
                <UserCheck className="w-4 h-4" />
                You're in this game
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleLeave}
                disabled={leaveMutation.isPending}
              >
                Leave Game
              </Button>
            </div>
          ) : (
            <Button className="w-full" disabled>
              Not Joinable
            </Button>
          )}
        </div>
      </div>

      {/* ─── Floating Chat Button (mobile) ────────────────── */}
      <button
        onClick={() => setChatOpen(true)}
        className="fixed bottom-20 right-4 z-30 w-12 h-12 rounded-full bg-neon text-background flex items-center justify-center shadow-lg hover:bg-neon-light transition-colors lg:hidden"
      >
        <MessageCircle className="w-5 h-5" />
        {(chatCount ?? 0) > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center font-bold">
            {chatCount}
          </span>
        )}
      </button>

      {/* ─── Chat Panel ───────────────────────────────────── */}
      <ChatPanel gameId={game.id} isOpen={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Backdrop when chat is open */}
      {chatOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={() => setChatOpen(false)}
        />
      )}
    </div>
  );
}
